/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/common/ConnectionsList" />
import * as React from "react";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { memoize } from "VSS/Core/Util/Memoize";
import { equals, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { ListSelection } from "VSSUI/List";
import { IMenuItem } from "VSSUI/Menu";
import { Status as StatusComponent, Statuses, StatusSize } from "VSSUI/Status";
import { ColumnFill, ColumnMore, ITableColumn, SimpleTableCell, Table, TableColumnStyle } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { Location } from "VSSUI/Utilities/Position";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ConnectionErrorType, ExternalConnection } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { IConnectionsListProps } from "Wit/Settings/BoardsExternalIntegration/components/common/ConnectionsList.Props";
import { Panel } from "Wit/Settings/BoardsExternalIntegration/components/panels/Panel.enum";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { getFriendlyConnectionName, getProviderKey } from "Wit/Settings/BoardsExternalIntegration/utils";
import { IdentityView } from "Wit/UI/IdentityView";

interface IConnectionListItem {
    connection: ExternalConnection;
    repos: ExternalGitRepo[];
    authType: string;
    createdBy: IdentityRef;
    isActive: boolean;
}

export class ConnectionsList extends VssComponent<IConnectionsListProps> {
    private static readonly connectionColumnKey = "connection";
    private static readonly authTypeColumnKey = "authtype";
    private static readonly reposColumnKey = "repos";
    private static readonly createdByColumnKey = "createdby";

    @memoize
    private static getItems(connections?: ExternalConnection[]): IConnectionListItem[] {
        if (connections) {
            return connections.map(connection => ({
                connection,
                repos: connection.externalGitRepos.sort((a: ExternalGitRepo, b: ExternalGitRepo) => localeIgnoreCaseComparer(a.name, b.name)),
                authType: connection.serviceEndpoint.authorizationScheme,
                createdBy: connection.serviceEndpoint.createdBy,
                isActive: !connection.statusMessage && !connection.friendlyStatusMessage
            }));
        } else {
            return [];
        }
    }
    private _columns: ITableColumn<IConnectionListItem>[];
    private _currentLoggedInUser: IdentityRef;
    private _selection = new ListSelection({ selectOnFocus: false, multiSelect: false });
    private _currentActivatedRow: number;

    constructor(props: IConnectionsListProps, context: IVssComponentContext) {
        super(props, context);

        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        this._currentLoggedInUser = vssPageService.getData()!.user;
        this._columns = this._getColumns();
    }

    public render(): JSX.Element {
        const items = ConnectionsList.getItems(this.props.connections).sort((a: IConnectionListItem, b: IConnectionListItem) => {
            const nameCompareResult = localeIgnoreCaseComparer(getFriendlyConnectionName(a.connection), getFriendlyConnectionName(b.connection));
            return nameCompareResult === 0 ? localeIgnoreCaseComparer(a.connection.providerKey, b.connection.providerKey) : nameCompareResult;
        });

        const provider = new ArrayItemProvider(items);
        return (
            <Table<IConnectionListItem>
                className={"connection-list-container"}
                columns={this._columns}
                itemProvider={provider}
                showLines={false}
                selectableText={true}
                selection={this._selection}
            />
        );
    }

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IConnectionListItem>,
        tableItem: IConnectionListItem
    ): JSX.Element => {
        const isGitHubApp: boolean = this._isGitHubAppScheme(tableItem);
        const statusText: string = tableItem.isActive
            ? Resources.ConnectionStatusActive
            : this._getInactiveConnectionStatusText(tableItem.connection, isGitHubApp);
        const serviceEndpointTypeName = this._getServiceEndpointType(tableItem);

        return (
            <SimpleTableCell key={"col-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                <div className="connection-list-primary-cell-titleCellTwoLine">
                    <div className="connection-list-primary-cell-titleCellIcon">
                        <Tooltip
                            anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                            text={statusText}
                            tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
                        >
                            <span>
                                <span data-is-focusable={true}>
                                    <VssIcon iconName="GitHubLogo" iconType={VssIconType.fabric} />
                                </span>
                                <span className="status-icon absolute">
                                    {tableItem.isActive && <StatusComponent {...Statuses.Success} size={StatusSize.m} />}
                                    {!tableItem.isActive && <StatusComponent {...Statuses.Failed} size={StatusSize.m} />}
                                </span>
                            </span>
                        </Tooltip>
                    </div>
                    <div className="connection-list-primary-cell-titleCellTwoLineText">
                        <div className="connection-list-primary-cell-titleCellPrimary">
                            <span className="connection-list-primary-cell-titleCellText">{getFriendlyConnectionName(tableItem.connection)}</span>
                        </div>
                        <Tooltip overflowOnly={true} text={serviceEndpointTypeName}>
                            <div className="connection-list-primary-cell-titleCellSecondary">{serviceEndpointTypeName}</div>
                        </Tooltip>
                    </div>
                </div>
            </SimpleTableCell>
        );
    };

    private _renderIdentityColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IConnectionListItem>,
        tableItem: IConnectionListItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <IdentityView className="gh-conn-created-by" identityRef={tableItem.createdBy} size="small-plus" />
            </SimpleTableCell>
        );
    };

    private _renderSimpleColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IConnectionListItem>,
        tableItem: IConnectionListItem
    ): JSX.Element => {
        const authType = this._getAuthType(tableItem.authType);
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span>{authType}</span>
            </SimpleTableCell>
        );
    };

    private _renderRepoColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IConnectionListItem>,
        tableItem: IConnectionListItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} className="connection-list-repo-column" key={"col-" + columnIndex}>
                {this._getReposElement(tableItem.repos)}
            </SimpleTableCell>
        );
    };

    private _getColumns(): ITableColumn<IConnectionListItem>[] {
        const columns: ITableColumn<IConnectionListItem>[] = [
            {
                id: ConnectionsList.connectionColumnKey,
                name: Resources.ColumnNameConnection,
                renderCell: this._renderNameColumn,
                columnStyle: TableColumnStyle.Primary,
                readonly: true,
                width: new ObservableValue(300),
                onSize: this._onSizeColumn
            }
        ];

        if (this.props.hasWritePermission) {
            columns.push(new ColumnMore(
                (listItem: IConnectionListItem) => {
                    return {
                        id: "sub-menu",
                        items: this._getMenuItems(listItem)
                    };
                },
                undefined,
                (rowIndex: number, columnIndex: number) => {
                    this._currentActivatedRow = rowIndex;
                }
            ) as ITableColumn<IConnectionListItem>);
        }

        columns.push(
            {
                id: ConnectionsList.authTypeColumnKey,
                name: Resources.ColumnNameAuthType,
                renderCell: this._renderSimpleColumn,
                columnStyle: TableColumnStyle.Secondary,
                readonly: true,
                width: new ObservableValue(200),
                onSize: this._onSizeColumn
            },
            {
                id: ConnectionsList.reposColumnKey,
                name: Resources.ColumnNameRepositories,
                renderCell: this._renderRepoColumn,
                columnStyle: TableColumnStyle.Secondary,
                readonly: true,
                width: new ObservableValue(400),
                onSize: this._onSizeColumn
            },
            {
                id: ConnectionsList.createdByColumnKey,
                name: Resources.ColumnNameCreatedBy,
                renderCell: this._renderIdentityColumn,
                columnStyle: TableColumnStyle.Secondary,
                readonly: true,
                width: new ObservableValue(250),
                onSize: this._onSizeColumn
            }
        );

        columns.push({
            ...ColumnFill,
            width: -0.01
        });

        return columns;
    }

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<IConnectionListItem>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _getReposElement = (repos: ExternalGitRepo[]): JSX.Element => {
        if (repos.length > 0) {
            const reposLength = repos.length;
            const firstRepoName = repos[0].name;

            if (reposLength > 1) {
                const _onRenderReposTooltipContent = (): JSX.Element => {
                    const reposToDisplayInTooltip: JSX.Element[] = repos
                        .slice(0, 10)
                        .map((repo, index) => <li key={repo.id + index}>{repo.name}</li>);

                    const additionalReposToolTipCount = reposLength - 10;
                    if (additionalReposToolTipCount > 0) {
                        reposToDisplayInTooltip.push(
                            <li className="repolist-item" key={"nmoreusers"}>
                                {format(Resources.ReposToolTipMoreRepos, additionalReposToolTipCount)}
                            </li>
                        );
                    }
                    return (
                        <div className="repolist-tooltip">
                            <ul className="repo-list">{reposToDisplayInTooltip}</ul>
                        </div>
                    );
                };

                const repoMoreText = `, ${reposLength - 1} ${Resources.MoreReposText}`;

                return (
                    <Tooltip
                        anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                        renderContent={_onRenderReposTooltipContent}
                        showOnFocus={true}
                        tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
                    >
                        <div data-is-focusable={true} className="flex">
                            <span className="flex flex-shrink repo-name-col-content repo-name-col-first-name">{firstRepoName}</span>
                            <span className="flex repo-name-col-content repo-name-col-more">{repoMoreText}</span>
                        </div>
                    </Tooltip>
                );
            } else {
                return (
                    <Tooltip
                        anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                        overflowOnly={true}
                        showOnFocus={true}
                        text={firstRepoName}
                        tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
                    >
                        <div className="flex flex-grow text-ellipsis" data-is-focusable={true}>
                            {firstRepoName}
                        </div>
                    </Tooltip>
                );
            }
        } else {
            return <div className="flex flex-grow empty-repo-list">{`(${Resources.ConnectionsList_Repos_EmptyText})`}</div>;
        }
    };

    private _getServiceEndpointType = (item: IConnectionListItem): string => {
        const { type, url } = item.connection.serviceEndpoint;
        const { providerKey } = item.connection;

        let typeName = Resources.ConnectionServiceEndpointTypeName_Unknown;

        // Service endpoint doesn't enforce type sensitivity
        switch (type.toLowerCase()) {
            case GitHubConnectionConstants.EnterpriseServiceEndpointType:
                if (url) {
                    typeName = getProviderKey(url);
                } else {
                    typeName = providerKey;
                }
                break;
            case GitHubConnectionConstants.GitHubBoardsServiceEndpointType:
            case GitHubConnectionConstants.GitHubServiceEndpointType:
                typeName = "GitHub";
                break;
        }

        return typeName;
    };

    private _getMenuItems = (connectionListItem: IConnectionListItem): IMenuItem[] => {
        const isGitHubApp: boolean = this._isGitHubAppScheme(connectionListItem);
        const canUserAddRepo =
            (isGitHubApp && this.props.hasWritePermission) || equals(this._currentLoggedInUser.id, connectionListItem.createdBy.id, true);
        const performOAuthCheck: boolean = isGitHubApp;
        return [
            {
                id: "addrepos",
                text: canUserAddRepo ? Resources.AddRepos : Resources.AddRepos_NoPermission,
                disabled:
                    !canUserAddRepo ||
                    (!connectionListItem.isActive && connectionListItem.connection.connectionErrorType !== ConnectionErrorType.NoRepositories),
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    // For GitHub App when FF is on, we will use Org selection panel to start unified experience
                    const panel = equals(
                        connectionListItem.connection.serviceEndpoint.authorizationScheme,
                        GitHubConnectionConstants.GitHubAppScheme,
                        true /* ignorecase */
                    )
                        ? Panel.OrgSelection
                        : Panel.AddRepo;
                    this.props.showPanel(
                        panel,
                        connectionListItem.connection,
                        () => this._setRowFocus(connectionListItem),
                        performOAuthCheck,
                        isGitHubApp
                    );
                }
            },
            {
                id: "removerepos",
                text: Resources.RemoveRepos,
                disabled:
                    !connectionListItem.isActive &&
                    connectionListItem.connection.connectionErrorType !== ConnectionErrorType.RepositoriesMappedToDifferentOrganization,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this.props.showPanel(
                        Panel.RemoveRepo,
                        connectionListItem.connection,
                        () => this._setRowFocus(connectionListItem),
                        false,
                        isGitHubApp
                    );
                }
            },
            {
                id: "removeconnection",
                text: Resources.RemoveConnection,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this.props.onOpenRemoveConnectionDialog(connectionListItem.connection, () => this._setRowFocus(connectionListItem));
                }
            }
        ];
    };

    private _setRowFocus(rowItem?: IConnectionListItem): void {
        this._selection.select(this._currentActivatedRow);
    }

    private _getAuthType(authType: string): string {
        if (
            equals(authType, GitHubConnectionConstants.PersonalAccessTokenScheme, true) ||
            equals(authType, GitHubConnectionConstants.EnterprisePersonalAccessTokenScheme, true)
        ) {
            return Resources.ColumnAuthTypePAT;
        } else if (equals(authType, GitHubConnectionConstants.GitHubAppScheme, true)) {
            return Resources.GitHubApp;
        } else if (equals(authType, GitHubConnectionConstants.EnterpriseCredentialsScheme, true)) {
            return Resources.ColumnAuthTypeCredentials;
        } else if (equals(authType, GitHubConnectionConstants.EnterpriseOAuthScheme, true)) {
            return Resources.OAuth;
        }

        return authType;
    }

    private _isGitHubAppScheme(connectionListItem: IConnectionListItem): boolean {
        return equals(connectionListItem.authType, GitHubConnectionConstants.GitHubAppScheme, true);
    }

    private _getInactiveConnectionStatusText(connection: ExternalConnection, isGitHubApp: boolean): string {
        if (connection.friendlyStatusMessage) {
            return connection.friendlyStatusMessage;
        }

        return isGitHubApp ? Resources.GitHubAppConnectionStatusInactive : Resources.ConnectionStatusInactive;
    }
}
